import TinyMCE from '@components/TinyMCE';
import WolfInput from '@components/ui/WolfInput';
import WolfTextarea from '@components/ui/WolfTextarea';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import ProcessesGraphQL from '@graphql/process.queries';
import { useTranslation } from '@hooks/useTranslation';
import { getProcessesWithAreaServiceRequest, LinkedProcess } from '@models/process.model';
import { ConfirmationType, StepType, TimingType } from '@models/step.model';
import { InfoOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Grid, IconButton, TextField, Typography, useMediaQuery } from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import WolfDropdown from './ui/WolfDropdown';

interface ProcessContentProps {
  newStepType?: StepType | null;
  parentCategory?: string;
  initialStepName: string;
  initialLinkedProcess: { processId: string; name: string };
  initialNotes: string;
  initialCondition: string;
  initialDescription: string;
  initialTimingType: TimingType;
  initialTimingName: string;
  initialTimingDescription: string;
  initialData?: any;
  updatingStep?: any;
  isConditionStep?: boolean;
}

interface ProcessListItem {
  processId: string;
  name: string;
  label: string;
  areaName?: string;
  serviceName?: string;
  categoryName?: string;
  processName?: string;
  service?: {
    name?: string;
    area?: {
      name?: string;
    };
  };
  category?: string;
}

export interface ProcessContentRef {
  focusNameField: () => void;
}

const ProcessContent = forwardRef<ProcessContentRef, ProcessContentProps>(
  (
    {
      newStepType = null,
      parentCategory = '',
      initialStepName,
      initialLinkedProcess,
      initialNotes,
      initialCondition,
      initialDescription,
      initialTimingType,
      initialTimingName,
      initialTimingDescription,
      initialData,
      updatingStep,
      isConditionStep = false,
    },
    ref,
  ) => {
    const editorRef: any = useRef(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const localeProcessForm = useTranslation('processForm');
    const localeCommon = useTranslation('common');
    const localeStepItem = useTranslation('stepItem');
    const localeProcesses = useTranslation('processes');
    const isLargeScreen = useMediaQuery('(min-width:1536px)');

    const [stepName, setStepName] = useState(initialStepName);
    const [linkedProcess, setLinkedProcess] = useState<LinkedProcess>(initialLinkedProcess);
    const [notes, setNotes] = useState(initialNotes);
    const [condition, setCondition] = useState(initialCondition);
    const [description, setDescription] = useState(initialDescription);
    const [confirmationType, setConfirmationType] = useState<ConfirmationType>(
      initialData?.stepData?.confirmationType || ConfirmationType.NONE
    );
    const [confirmationQuestion, setConfirmationQuestion] = useState(
      initialData?.stepData?.confirmationQuestion || ''
    );
    const [processesList, setProcessesList] = useState<ProcessListItem[]>([]);
    const [timingType, setTimingType] = useState<{ value: TimingType; text: string }>({
      value: TimingType.IMMEDIATE,
      text: localeStepItem['immediate'],
    });
    const [timingName, setTimingName] = useState(initialTimingName);
    const [timingDescription, setTimingDescription] = useState(initialTimingDescription);

    const timingTypes: { text: string; value: TimingType }[] = [
      { value: TimingType.IMMEDIATE, text: localeStepItem['immediate'] },
      { value: TimingType.EMAIL, text: localeStepItem['linkEmail'] },
      { value: TimingType.CALENDAR, text: localeStepItem['linkCalendar'] },
      { value: TimingType.OTHER, text: localeStepItem['linkOther'] },
    ];

    useImperativeHandle(ref, () => ({
      focusNameField: () => {
        // Add a delay to ensure the component is fully rendered
        setTimeout(() => {
          if (nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
          }
        }, 150);
      },
    }));

    useEffect(() => {
      setLinkedProcess(initialLinkedProcess);
      updatingStep({ linked: initialLinkedProcess });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLinkedProcess]);

    useEffect(() => {
      setStepName(initialStepName);
      updatingStep({ name: initialStepName });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialStepName]);

    useEffect(() => {
      setNotes(initialNotes);
      updatingStep({ notes: initialNotes });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialNotes]);

    useEffect(() => {
      setCondition(initialCondition);
      updatingStep({ condition: initialCondition });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCondition]);

    useEffect(() => {
      setDescription(initialDescription);
      updatingStep({ description: initialDescription });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDescription]);

    useEffect(() => {
      setTimingType({
        value: initialTimingType,
        text: timingTypes.find((item) => item.value === initialTimingType)?.text || localeStepItem['immediate'],
      });
      updatingStep({ timingType: initialTimingType });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTimingType]);

    useEffect(() => {
      setTimingName(initialTimingName);
      updatingStep({ timingName: initialTimingName });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTimingName]);

    useEffect(() => {
      setTimingDescription(initialTimingDescription);
      updatingStep({ timingDescription: initialTimingDescription });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTimingDescription]);

    useEffect(() => {
      getProcessesList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!stepName && initialData) {
        setStepName(initialData.name);
        updatingStep({ name: initialData.name });
      }
      if (initialData?.stepData?.confirmationType) {
        setConfirmationType(initialData.stepData.confirmationType || ConfirmationType.NONE)
      }
      if (initialData?.stepData?.confirmationQuestion) {
        setConfirmationQuestion(initialData.stepData.confirmationQuestion || '')
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    const handleStepNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setStepName(event.target.value);
      updatingStep({ name: event.target.value });
    };
    const handleLinkedProcessChange = (_event: any, selected: ProcessListItem | null) => {
      if (selected) {
        setLinkedProcess({
          name: selected.name,
          processId: selected.processId,
        });
        updatingStep({
          linked: {
            name: selected.name,
            processId: selected.processId,
          },
        });
      } else {
        setLinkedProcess({ name: '', processId: '' });
        updatingStep({
          linked: {
            name: '',
            processId: '',
          },
        });
      }
    };
    const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(event.target.value);
      updatingStep({ notes: event.target.value });
    };
    const handleConditionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCondition(event.target.value);
      updatingStep({ condition: event.target.value });
    };
    const handleConfirmationTypeChange = (value: ConfirmationType) => {
      setConfirmationType(value);
      updatingStep({
        stepData: {
          confirmationType: value
        }
      });
      // Clear question if switching to NONE
      if (value === ConfirmationType.NONE) {
        setConfirmationQuestion('');
        updatingStep({
          stepData: {
            confirmationQuestion: ''
          }
        });
      }
    };
    const handleConfirmationQuestionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setConfirmationQuestion(event.target.value);
      updatingStep({
        stepData: {
          confirmationQuestion: event.target.value
        }
      });
    };
    const handleDescriptionChange = (content: string) => {
      setDescription(content);
      updatingStep({ description: content });
    };

    const handleTimingChange = (timing: any) => {
      setTimingType(timing);
      if (timing.value !== TimingType.IMMEDIATE) {
        updatingStep({ timingType: timing.value });
      } else {
        updatingStep({ timingType: TimingType.IMMEDIATE, timingName: '', timingDescription: '' });
      }
    };

    const handleTimingNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setTimingName(event.target.value);
      updatingStep({ timingName: event.target.value });
    };

    const handleTimingDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTimingDescription(event.target.value);
      updatingStep({ timingDescription: event.target.value });
    };

    const getProcessesList = async () => {
      try {
        const data: getProcessesWithAreaServiceRequest = await graphQlClient.request(
          ProcessesGraphQL.queries.getProcessesWithAreaService,
        );
        const transformedOptions = data.getProcessesWithAreaService.map((item) => {
          const areaName = item.service?.area?.name ?? '';
          const serviceName = item.service?.name ?? '';
          const categoryName = item.category ?? '';
          const optionName = item.name ?? '';
          return {
            ...item,
            label: `${areaName} - ${serviceName} - ${categoryName} - ${optionName}`,
            areaName,
            serviceName,
            categoryName,
            processName: optionName,
          };
        });
        setProcessesList([
          ...transformedOptions.filter((item) => item.category === parentCategory),
          ...transformedOptions.filter((item) => item.category !== parentCategory),
        ]);
      } catch (e: any) {
        console.log('error', e);
      }
    };

    return (
      <Box className="w-full h-full grow h-full-minus-header">
        <Box className="p-4 xl:mx-5per 2xl:mx-10per">
          <Typography variant="h4semibold" className="!my-4">
            1.{' '}
            {isConditionStep
              ? localeProcessForm['conditionInformation']
              : newStepType === StepType.SWITCH
                ? localeProcessForm['caseInformation']
                : newStepType === StepType.REPEAT
                  ? localeProcessForm['loopInformation']
                  : <>
                      {localeProcessForm['stepInformationPre']}
                      <strong>{localeProcessForm['stepInformationBold']}</strong>
                      {localeProcessForm['stepInformationPost']}
                    </>}
          </Typography>
          <Box className="mx-auto">
            <Grid
              container
              spacing={4}
              maxWidth={isLargeScreen ? '100%' : 500}
              className={!isLargeScreen ? '!mx-auto' : ''}>
              <Grid item xs={12} xl={6}>
                <Box className="mb-6">
                  <WolfInput
                    label={
                      (isConditionStep
                        ? localeProcessForm['conditionName']
                        : newStepType === StepType.SWITCH
                          ? localeProcessForm['caseName']
                          : newStepType === StepType.REPEAT
                            ? localeProcessForm['loopName']
                            : localeProcessForm['stepName']) + ' *'
                    }
                    placeholder={
                      isConditionStep
                        ? localeProcessForm['conditionName']
                        : newStepType === StepType.SWITCH
                          ? localeProcessForm['caseName']
                          : newStepType === StepType.REPEAT
                            ? localeProcessForm['loopName']
                            : localeProcessForm['stepName']
                    }
                    value={stepName}
                    onChange={handleStepNameChange}
                    secondaryLabel={localeCommon['required']}
                    inputRef={nameInputRef}
                    fullWidth
                  />
                </Box>
                {!(StepType.SWITCH === newStepType || StepType.REPEAT === newStepType) && (
                  <>
                    <Box className="mb-6">
                      <Box className="flex justify-between mb-0">
                        <Typography variant="body14semibold" color="primary">
                          {localeProcessForm['linkedProcess']}
                          <WolfTooltip
                            title={
                              localeProcessForm['linkedProcessTooltip'] ||
                              'Optional field to specify a process that follows after this step'
                            }>
                            <IconButton size="small">
                              <InfoOutlined sx={{ width: 18, height: 18 }} className="text-primaryDark" />
                            </IconButton>
                          </WolfTooltip>
                        </Typography>
                        <Typography variant="body14" color="primary">
                          {localeCommon['optional']}
                        </Typography>
                      </Box>
                      <Autocomplete
                        className=""
                        value={
                          linkedProcess?.processId
                            ? processesList.find((item) => item.processId === linkedProcess.processId) || null
                            : null
                        }
                        onChange={handleLinkedProcessChange}
                        options={processesList}
                        isOptionEqualToValue={(option, value) => option?.processId === value?.processId}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          return `${option.processName} (${option.serviceName} - ${option.areaName})`;
                        }}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} className="!py-3 !px-4">
                            <Box className="w-full">
                              <Typography variant="body12" color="text.secondary" className="!mb-2">
                                {option.areaName} • 
                              </Typography>
                              <Typography variant="body12" color="text.secondary" className="!mb-2">
                                {option.serviceName} • {option.categoryName || 'Uncategorized'}  • 
                              </Typography>
                              <Typography variant="body14semibold" color="primary" className="!font-semibold">
                                {option.processName}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            className="!h-10"
                            placeholder={localeProcessForm['selectProcess']}
                          />
                        )}
                        filterOptions={(options, { inputValue }) => {
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter(
                            (option) =>
                              option?.processName?.toLowerCase().includes(searchTerm) ||
                              option?.serviceName?.toLowerCase().includes(searchTerm) ||
                              option?.areaName?.toLowerCase().includes(searchTerm) ||
                              option?.categoryName?.toLowerCase().includes(searchTerm),
                          );
                        }}
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            padding: '2px !important',
                            paddingRight: '54px !important',
                            height: '40px',
                            minHeight: '40px',
                          },
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                          },
                          '& .MuiAutocomplete-input': {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          },
                          '& .MuiAutocomplete-endAdornment': {
                            right: '9px',
                          },
                          '& .MuiAutocomplete-option': {
                            minHeight: '80px !important',
                          },
                        }}
                      />
                    </Box>

                    {linkedProcess?.processId && (
                      <>
                        <Box className="mb-6">
                          <Box className="flex justify-between mb-0">
                            <Typography variant="body14semibold" color="primary">
                              {localeStepItem['linkedProcessTiming']}
                              <WolfTooltip title={localeStepItem['linkedProcessTimingTooltip']}>
                                <IconButton size="small">
                                  <InfoOutlined sx={{ width: 18, height: 18 }} className="text-primaryDark" />
                                </IconButton>
                              </WolfTooltip>
                            </Typography>
                          </Box>
                          <Box className="border-2 border-primaryIcy border-solid rounded">
                            <WolfDropdown
                              label={timingType.text || '' || localeStepItem['linkedProcessTiming']}
                              items={timingTypes}
                              initialSelectedItems={[timingType.value]}
                              singleSelect={true}
                              onChange={(timing) => handleTimingChange(timing[0])}
                            />
                          </Box>
                        </Box>
                        {timingType.value !== TimingType.IMMEDIATE && (
                          <Box>
                            <Box className="mb-6">
                              <WolfInput
                                label={
                                  timingType.value === TimingType.EMAIL
                                    ? localeStepItem['triggerNameMessage']
                                    : timingType.value === TimingType.CALENDAR
                                      ? localeStepItem['triggerNameCalendar']
                                      : localeStepItem['triggerNameOther']
                                }
                                value={timingName}
                                onChange={handleTimingNameChange}
                                fullWidth
                                tooltipText={
                                  timingType.value === TimingType.EMAIL
                                    ? localeStepItem['triggerNameMessageTooltip']
                                    : timingType.value === TimingType.CALENDAR
                                      ? localeStepItem['triggerNameCalendarTooltip']
                                      : localeStepItem['triggerNameOtherTooltip']
                                }
                              />
                            </Box>

                            <Box className="mb-6">
                              <WolfTextarea
                                label={
                                  timingType.value === TimingType.EMAIL
                                    ? localeStepItem['triggerDescriptionMessage']
                                    : timingType.value === TimingType.CALENDAR
                                      ? localeStepItem['triggerDescriptionCalendar']
                                      : localeStepItem['triggerDescriptionOther']
                                }
                                value={timingDescription}
                                onChange={handleTimingDescriptionChange}
                                fullWidth
                                tooltipText={
                                  timingType.value === TimingType.EMAIL
                                    ? localeStepItem['triggerDescriptionMessageTooltip']
                                    : timingType.value === TimingType.CALENDAR
                                      ? localeStepItem['triggerDescriptionCalendarTooltip']
                                      : localeStepItem['triggerDescriptionOtherTooltip']
                                }
                              />
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Grid>
              <Grid item xs={12} xl={6} className="!pt-0">
                {!(StepType.SWITCH === newStepType || StepType.REPEAT === newStepType) && (
                  <>
                    <Box className="mb-6">
                      <WolfTextarea
                        label={localeProcessForm['stepNotes']}
                        secondaryLabel={localeCommon['optional']}
                        placeholder={localeProcessForm['stepNotesExplain']}
                        value={notes}
                        customRows={2}
                        onChange={handleNotesChange}
                        tooltipText={
                          localeProcessForm['stepNotesTooltip'] ||
                          'Optional field to clarify extra information for the step'
                        }
                        fullWidth
                      />
                    </Box>
                    <Box className="mb-6">
                      <WolfTextarea
                        label={localeProcessForm['stepCondition']}
                        secondaryLabel={localeCommon['optional']}
                        placeholder={localeProcessForm['stepConditionExplain']}
                        value={condition}
                        customRows={2}
                        onChange={handleConditionChange}
                        tooltipText={
                          localeProcessForm['stepConditionTooltip'] ||
                          'Optional field to specify requirements for executing or not executing this step'
                        }
                        fullWidth
                      />
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>

          {!(StepType.SWITCH === newStepType || StepType.REPEAT === newStepType) && (
            <Box className="mt-8">
              <Box className="flex items-center gap-1 !my-4">
                <Typography variant="h4semibold">
                  2. {localeProcessForm['stepContent']}
                </Typography>
                <WolfTooltip title={localeProcessForm['stepContentTooltip']}>
                  <IconButton size="small">
                    <InfoOutlined sx={{ width: 18, height: 18 }} className="text-primaryDark" />
                  </IconButton>
                </WolfTooltip>
              </Box>
              <Box className="w-full">
                <TinyMCE
                  value={description || ''}
                  handleDescriptionChange={handleDescriptionChange}
                  editorRef={editorRef}
                />
              </Box>
            </Box>
          )}

          {/* Step Control Section - At the bottom */}
            <Box className="mt-8">
              <Box className="">
                {/* Divider */}
                <Box className="mb-6">
                  <hr className="border-t border-gray-200" />
                </Box>

                {/* Step Control Section */}
                <Box className="mb-32">
                  <Typography variant="h4semibold" className="mb-4">
                    {StepType.SWITCH === newStepType || StepType.REPEAT === newStepType ? '2' : '3'}. {localeProcesses['stepControl']}
                  </Typography>


                  {/* Side by side layout - Full width */}
                  <Box className="flex gap-6 w-full">
                    {/* Confirmation Type Radio Buttons - 1/3 width */}
                    <Box className="flex flex-col gap-2 w-1/2">
                      <Box className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="conf-none"
                          name="confirmationType"
                          checked={confirmationType === ConfirmationType.NONE}
                          onChange={() => handleConfirmationTypeChange(ConfirmationType.NONE)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label htmlFor="conf-none" style={{ cursor: 'pointer' }}>
                          <Typography variant="body14">{localeProcesses['confirmationTypeNone']}</Typography>
                        </label>
                      </Box>
                      <Box className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="conf-boolean"
                          name="confirmationType"
                          checked={confirmationType === ConfirmationType.BOOLEAN}
                          onChange={() => handleConfirmationTypeChange(ConfirmationType.BOOLEAN)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label htmlFor="conf-boolean" style={{ cursor: 'pointer' }}>
                          <Typography variant="body14">{localeProcesses['confirmationTypeBoolean']}</Typography>
                        </label>
                      </Box>
                      <Box className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="conf-custom"
                          name="confirmationType"
                          checked={confirmationType === ConfirmationType.CUSTOM_TEXT}
                          onChange={() => handleConfirmationTypeChange(ConfirmationType.CUSTOM_TEXT)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label htmlFor="conf-custom" style={{ cursor: 'pointer' }}>
                          <Typography variant="body14">{localeProcesses['confirmationTypeCustomText']}</Typography>
                        </label>
                      </Box>
                    </Box>

                    {/* Confirmation Question - 2/3 width (shown when not NONE) */}
                    {confirmationType !== ConfirmationType.NONE && (
                      <Box className="w-1/2">
                        <WolfTextarea
                          label={
                            confirmationType === ConfirmationType.BOOLEAN
                              ? localeProcesses['checkboxControlLabel']
                              : localeProcesses['customTextLabel']
                          }
                          placeholder={
                            confirmationType === ConfirmationType.BOOLEAN
                              ? localeProcesses['confirmationQuestionBooleanPlaceholder']
                              : localeProcesses['confirmationQuestionPlaceholder']
                          }
                          value={confirmationQuestion}
                          customRows={2}
                          onChange={handleConfirmationQuestionChange}
                          fullWidth
                          inputProps={{
                            style: { fontSize: '12px' }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
        </Box>
      </Box>
    );
  },
);

ProcessContent.displayName = 'ProcessContent';

export default ProcessContent;
